import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ArrowDownCircle, Edit, MinusCircle, Plus, Scale, Search, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { inventoryApi, fmtQty } from './inventoryApi';
import { SupplyFormDialog } from './SupplyFormDialog';
import { MovementDialog } from './MovementDialog';

const SupplyRow = ({ s, onMove, onEdit, onDelete }) => {
  const out = s.stock <= 0;
  return (
    <tr className="border-t border-gray-800 text-sm" data-testid={`supply-row-${s.id}`}>
      <td className="py-2 px-3 text-white font-medium">{s.name}</td>
      <td className="py-2 px-3 text-right">
        <span className={`font-bold ${out ? 'text-red-400' : s.low ? 'text-amber-400' : 'text-emerald-300'}`} data-testid={`supply-stock-${s.id}`}>
          {fmtQty(s.stock)} {s.unit}
        </span>
        {(out || s.low) && (
          <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${out ? 'bg-red-900/50 text-red-300' : 'bg-amber-900/50 text-amber-300'}`}>
            {out ? 'AGOTADO' : 'BAJO'}
          </span>
        )}
      </td>
      <td className="py-2 px-3 text-right text-gray-400">{fmtQty(s.min_stock)} {s.unit}</td>
      <td className="py-2 px-3 text-right text-gray-300">${Number(s.cost_per_unit || 0).toFixed(2)}</td>
      <td className="py-2 px-3 text-right text-gray-300">${(Math.max(0, s.stock) * (s.cost_per_unit || 0)).toFixed(2)}</td>
      <td className="py-2 px-3">
        <div className="flex gap-1 justify-end">
          <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-600 text-xs" onClick={() => onMove(s, 'entrada')} data-testid={`supply-entrada-${s.id}`}><ArrowDownCircle className="h-3.5 w-3.5 mr-1" />Entrada</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs border-blue-600/50 text-blue-300" onClick={() => onMove(s, 'ajuste')} data-testid={`supply-ajuste-${s.id}`}><Scale className="h-3.5 w-3.5 mr-1" />Conteo</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs border-red-600/50 text-red-300" onClick={() => onMove(s, 'merma')} data-testid={`supply-merma-${s.id}`}><MinusCircle className="h-3.5 w-3.5 mr-1" />Merma</Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-1 text-gray-300" onClick={() => onEdit(s)} data-testid={`supply-edit-${s.id}`}><Edit className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-1 text-red-400" onClick={() => onDelete(s)} data-testid={`supply-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </td>
    </tr>
  );
};

export const SuppliesPanel = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(null);

  const load = useCallback(async () => {
    try { setItems(await inventoryApi.items()); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar el insumo "${s.name}"? Se quitará de todas las recetas.`)) return;
    try { await inventoryApi.deleteItem(s.id); toast.success('Insumo eliminado'); refresh(); } catch (e) { toast.error(e.message); }
  };

  const filtered = useMemo(() => items.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) && (!onlyLow || s.low)), [items, search, onlyLow]);
  const lowCount = items.filter((s) => s.low).length;
  const totalValue = items.reduce((t, s) => t + Math.max(0, s.stock) * (s.cost_per_unit || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar insumo..." className="pl-9 bg-black/50 border-gray-700 text-white" data-testid="supply-search" />
        </div>
        <button onClick={() => setOnlyLow((v) => !v)} data-testid="supply-filter-low"
          className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm border ${onlyLow ? 'bg-amber-600 border-amber-500 text-white' : 'border-amber-600/40 text-amber-300'}`}>
          <AlertTriangle className="h-4 w-4" /> Stock bajo ({lowCount})
        </button>
        <span className="text-gray-400 text-sm">Valor inventario: <b className="text-emerald-300" data-testid="inventory-total-value">${totalValue.toFixed(2)}</b></span>
        <Button onClick={() => setEditing({})} className="bg-emerald-600 hover:bg-emerald-700" data-testid="supply-new-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo insumo</Button>
      </div>
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-center py-10" data-testid="supply-empty">No hay insumos. Crea el primero (ej. "Pieza de pollo", "Coca-Cola 600ml").</p>
        ) : (
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="text-gray-500 text-xs">
                <th className="py-2 px-3 text-left font-medium">Insumo</th>
                <th className="py-2 px-3 text-right font-medium">Existencia</th>
                <th className="py-2 px-3 text-right font-medium">Mínimo</th>
                <th className="py-2 px-3 text-right font-medium">Costo unit.</th>
                <th className="py-2 px-3 text-right font-medium">Valor</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <SupplyRow key={s.id} s={s} onMove={(item, type) => setMoving({ item, type })} onEdit={setEditing} onDelete={remove} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <SupplyFormDialog supply={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
      {moving && <MovementDialog item={moving.item} type={moving.type} onClose={() => setMoving(null)} onSaved={refresh} />}
    </div>
  );
};
